import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  SectionList,
  TouchableOpacity,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import BackArrow from '@assets/svg/BackArrow';
import FiltersIcon from '@assets/svg/FiltersIcon';
import { getTransactionStatement } from '@services/api';
import { ScaledSheet } from '@utils/scale';
import FilterModal from "@screens/MyTasks/components/FilterModal";

export default function TransactionStatements({route}) {
  const navigation = useNavigation();
  const { type = '', date_range = '' } = route.params;
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('DATE');
  const [filterModalVisible, setFilterModalVisible] = useState(false);

  const capitalizeStr = (str) => {
    if (str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
    return str;
  };

  const loadTransactions = async (filters) => {
    setIsLoading(true);
    try {
      const { data } = await getTransactionStatement(filters);
      setData(data);
      if (data?.applied_filters) {
        setSelectedFilter(data.applied_filters);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = (overrides = {}) => {
    const newFilters = { ...selectedFilter, ...overrides };
    setSelectedFilter(newFilters);
    const filters = {
      date_range: newFilters.DATE?.key || '',
      type: newFilters.TYPE?.key || '',
    };
    loadTransactions(filters);
  };

  useFocusEffect(
    useCallback(() => {
      if (type || date_range) {
        applyFilters({ TYPE: { key: type }, DATE: { key: date_range } });
      } else {
        applyFilters();
      }
    }, [type])
  );

  const handleTransactionPress = (item) => {
    if (item.type === 'WITHDRAWAL') {
      console.log('Withdrawal pressed:', item.id);
      if (item.nav_to) {
        navigation.navigate(item.nav_to, { transactionId: item.id });
      }
    }
  };

  const renderItem = ({ item }) => {
    const isWithdrawal = item.type === 'WITHDRAWAL';
    return (
      <Pressable
        style={({ pressed }) => [
          styles.transactionItem,
          isWithdrawal && pressed && styles.pressed,
        ]}
        disabled={!isWithdrawal}
        onPress={isWithdrawal ? () => handleTransactionPress(item) : undefined}
      >
        <View style={styles.transactionMainRow}>
          <View style={styles.transactionLeft}>
            <View style={styles.transactionTitleRow}>
              <Text style={styles.transactionTitle}>{item.title}</Text>
              {isWithdrawal && <Text style={styles.chevron}>›</Text>}
            </View>
            <Text style={styles.transactionTime}>{item.timestamp_txt}</Text>
            {item?.subtitle_lines && item?.subtitle_lines?.length > 0 && (
              <View style={styles.subtitleContainer}>
                {item?.subtitle_lines?.map((line, index) => (
                  <Text key={index} style={styles.transactionId}>
                    {line}
                  </Text>
                ))}
              </View>
            )}
          </View>
          <View style={styles.transactionRight}>
            <Text
              style={[styles.transactionAmount, { color: item.status_color }]}
            >
              {item.value_txt}
            </Text>
            {item.status_txt && (
              <Text style={[styles.statusText, { color: item.status_color }]}>
                {capitalizeStr(item.status_txt)}
              </Text>
            )}
          </View>
        </View>

        {item.info_txt ? (
          <View style={styles.infoContainer}>
            <Text style={styles.infoText}>{item.info_txt}</Text>
          </View>
        ) : null}
      </Pressable>
    );
  };

  const renderSectionHeader = ({ section: { title } }) => {
    return (
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionHeaderText}>{title}</Text>
      </View>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <BackArrow />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Transaction Statement</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#10B981" />
        </View>
      </View>
    );
  }

  // Transform API response to SectionList format
  const sections = (data?.transactions || []).map(section => ({
    title: section.section_title,
    data: section.section_list || [],
    key: section.key
  }));

  const handleOptionPress = (item) => {
    setSelectedFilter((prev) => ({
      ...prev,
      [selectedCategory]: item
    }));
  };

  const getSelectedFilters = () => {
    return selectedFilter[selectedCategory] || {};
  };

  const clearFilters = () => {
    setSelectedFilter({});
    setSelectedCategory("DATE");
  };

  const metadata = {
    title: "फ़िल्टर",
    searchTermPlaceholder: "",
    submit_text: "लागू करें",
    clear_text: "साफ़ करें"
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <BackArrow />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{data?.title || 'Transaction Statement'}</Text>
      </View>

      {/* Filter Chips */}
      <View style={styles.filterContainer}>
        <Pressable onPress={() => {
          setFilterModalVisible(true)
        }}>
          <View
            style={styles.filterChip}
          >
            <FiltersIcon width={16} height={16} />
            <Text style={styles.filterText}>
              {'Filter'}
            </Text>
          </View>
        </Pressable>

        {data?.filters?.TYPE?.values?.map((filter) => (
          <TouchableOpacity
            key={filter.key}
            style={[
              styles.filterChip,
              selectedFilter?.TYPE?.key === filter.key && styles.filterChipSelected,
            ]}
            onPress={() => {
              const isSelected = selectedFilter?.TYPE?.key === filter.key;
              applyFilters({ TYPE: isSelected ? undefined : filter });
            }}
          >
            <Text
              style={[
                styles.filterText,
                selectedFilter?.TYPE?.key === filter.key && styles.filterTextSelected,
              ]}
            >
              {filter.value}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Transaction List */}
      <SectionList
        sections={sections}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        contentContainerStyle={styles.listContent}
        keyExtractor={(item, index) => item?.id || index.toString()}
        showsVerticalScrollIndicator={false}
        stickySectionHeadersEnabled={false}
        renderSectionFooter={() => <View style={{}} />}
        keyboardShouldPersistTaps="handled"
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No transactions found</Text>
          </View>
        }
      />

      <FilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        onSave={() => {
          applyFilters();
          setFilterModalVisible(false);
        }}
        filterData={data?.filters}
        handleOptionPress={handleOptionPress}
        getSelectedFilters={getSelectedFilters}
        clearFilters={clearFilters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        metadata={metadata}
      />
    </View>
  );
}

const styles = ScaledSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
    marginLeft: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 8,
    backgroundColor: '#FFFFFF',
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    backgroundColor: '#FFFFFF',
    gap: 4,
  },
  filterChipSelected: {
    backgroundColor: '#111827',
    borderColor: '#111827',
  },
  filterIcon: {
    fontSize: 14,
    color: '#6B7280',
  },
  filterText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  filterTextSelected: {
    color: '#FFFFFF',
  },
  listContent: {
    paddingBottom: 32,
  },
  sectionHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#F3F4F6',
  },
  sectionHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  transactionItem: {
    justifyContent: "space-between",
    alignItems: "stretch",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 16,
    paddingVertical: 16,
    marginHorizontal: 16,
    marginBottom: 8,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  transactionMainRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  pressed: {
    opacity: 0.7,
  },
  transactionLeft: {
    flex: 1,
    marginRight: 16,
  },
  transactionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    flexWrap: 'nowrap',
  },
  transactionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    flexShrink: 1,
    marginRight: 4,
  },
  chevron: {
    fontSize: 24,
    color: '#9CA3AF',
    marginLeft: 4,
    flexShrink: 0,
    lineHeight: 24,
    height: 24,
    textAlignVertical: 'center',
  },
  transactionTime: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 2,
  },
  subtitleContainer: {
    marginTop: 4,
  },
  transactionId: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 2,
  },
  transactionRight: {
    alignItems: 'flex-end',
  },
  transactionAmount: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 2,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    color: "#6B7280",
  },
  infoContainer: {
    marginTop: 8,
    padding: 8,
    backgroundColor: "#EFF6FF",
    borderRadius: 8,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  infoText: {
    fontSize: 12,
    color: "#1D4ED8",
    textAlign: "left",
  },
});
